import {mapApiError} from "../../shared/api/errors";
import {formatNumber} from "../../shared/utils/format";
import {canViewAppTexts} from "./appTextsContracts";
import {i18n} from "../../shared/i18n";


export const DOCUMENT_DRAFT_STORAGE_PREFIX = "sorpaAdminCountryDocumentDraft:v1";
export const DEFAULT_DOCUMENT_MAX_LENGTH = 200_000;

export const EMPTY_DOCUMENT_MESSAGE = i18n.t("sections.content.p327");
export const DOCUMENT_CONFLICT_MESSAGE =
    i18n.t("sections.content.p328");
export const DOCUMENT_FORBIDDEN_MESSAGE = i18n.t("sections.content.p329");

// Kinds are the keys of the legal design (`rules`, `policy`, later `consent_*`): the
// server lists them, the admin only shortens the tab labels it knows.
export const DEFAULT_DOCUMENT_KINDS = Object.freeze([
    {kind: "rules", get title() { return i18n.t("sections.content.p330"); }},
    {kind: "policy", get title() { return i18n.t("sections.content.p331"); }},
]);

const KIND_TAB_LABELS = Object.freeze({
    get rules() { return i18n.t("sections.content.p332"); },
    get policy() { return i18n.t("sections.content.p331"); },
});

// The same gate as «Тексты приложения»: the endpoints serve platform roles only, and
// «Владелец» also holds content.view.
export const canViewCountryDocuments = canViewAppTexts;

export const canEditCountryDocuments = ({list, view, hasAction} = {}) =>
    list?.can_edit === true
    && typeof hasAction === "function"
    && hasAction("content.manage")
    && (view === undefined || view === null || view.can_edit === true);

export const documentKinds = (list) => {
    const kinds = Array.isArray(list?.kinds) ? list.kinds.filter((item) => item && typeof item.kind === "string") : [];
    return kinds.length ? kinds : DEFAULT_DOCUMENT_KINDS;
};

export const documentKindLabel = (kind, title) => KIND_TAB_LABELS[kind] || title || kind;

export const documentMaxLength = (...sources) => {
    const found = sources.map((source) => Number(source?.max_length)).find((value) => Number.isFinite(value) && value > 0);
    return found || DEFAULT_DOCUMENT_MAX_LENGTH;
};

// Mirrors the server's only normalisation: CRLF and lone CR become LF, spaces and tabs
// at the end of each line go, and so does whitespace at the end of the text.
export const normalizeDocumentText = (value) => {
    if (value === null || value === undefined) return "";
    return String(value)
        .replace(/\r\n?/g, "\n")
        .replace(/[ \t]+(?=\n|$)/g, "")
        .trimEnd();
};

// Characters as the server counts them (code points, so an emoji is one).
const SURROGATE_PAIR = /[\uD800-\uDBFF][\uDC00-\uDFFF]/g;

export const documentLength = (value) => {
    const text = String(value ?? "");
    return text.length - (text.match(SURROGATE_PAIR) || []).length;
};

export const isDocumentDirty = (text, savedText) => normalizeDocumentText(text) !== normalizeDocumentText(savedText);

// Errors the server would answer with 400 anyway; checked locally so «Сохранить» can wait.
export const documentClientError = (text, maxLength = DEFAULT_DOCUMENT_MAX_LENGTH) => {
    const value = String(text ?? "");
    if (value.includes("\0")) return i18n.t("sections.content.p333");
    const length = documentLength(normalizeDocumentText(value));
    if (length > maxLength) return i18n.t("sections.content.p334", {v0: formatNumber(maxLength), v1: formatNumber(length)});
    return null;
};

export const documentCountry = (list, countryId) =>
    (Array.isArray(list?.countries) ? list.countries : []).find((country) => String(country?.id) === String(countryId ?? "")) || null;

export const documentSummary = (list, countryId, kind) => {
    const country = documentCountry(list, countryId);
    return (Array.isArray(country?.documents) ? country.documents : []).find((item) => item?.kind === kind) || null;
};

const SUMMARY_FIELDS = ["kind", "title", "text", "is_empty", "length", "revision", "updated_at", "updated_by"];

// After a save or a reload the list keeps the tab badges in step without refetching
// every text of every country.
export const patchDocumentsList = (list, view) => {
    const countryId = view?.country?.id;
    const kind = view?.kind;
    if (!list || !Array.isArray(list.countries) || countryId === undefined || countryId === null || !kind) return list;
    const summary = Object.fromEntries(SUMMARY_FIELDS.filter((field) => field in view).map((field) => [field, view[field]]));
    let touched = false;
    const countries = list.countries.map((country) => {
        if (String(country?.id) !== String(countryId)) return country;
        const documents = Array.isArray(country.documents) ? country.documents : [];
        const exists = documents.some((item) => item?.kind === kind);
        touched = true;
        return {
            ...country,
            documents: exists
                ? documents.map((item) => (item?.kind === kind ? {...item, ...summary} : item))
                : [...documents, summary],
        };
    });
    return touched ? {...list, countries} : list;
};

export const documentSaveMessage = (response) => {
    const status = response?.result?.status;
    if (status === "unchanged") return i18n.t("sections.content.p335");
    if (status === "cleared") return i18n.t("sections.content.p336", {v0: EMPTY_DOCUMENT_MESSAGE});
    return i18n.t("sections.content.p337");
};

export const documentsErrorMessage = (error) => {
    if (error?.response?.status === 403) return DOCUMENT_FORBIDDEN_MESSAGE;
    const serverMessage = error?.response?.data?.message;
    if (typeof serverMessage === "string" && serverMessage.trim()) return serverMessage.slice(0, 500);
    return mapApiError(error).message;
};

export const documentDraftStorageKey = ({countryId, kind}) => `${DOCUMENT_DRAFT_STORAGE_PREFIX}:${countryId}:${kind}`;

// A stored draft = the typed text and the revision it was started from.
export const storedDocumentDraft = (stored) => {
    if (!stored || typeof stored.text !== "string") return null;
    return {
        text: stored.text,
        baseRevision: typeof stored.baseRevision === "string" ? stored.baseRevision : null,
        savedAt: typeof stored.savedAt === "string" ? stored.savedAt : null,
    };
};
